// Password recovery challenges never enter the login-token store.
export function interpretPasswordResetResponse(response, stage) {
  if (!response || ![0, 1, "0", "1"].includes(response.returncode)) {
    return { kind: "invalid" };
  }
  const data = response.returndata;
  if (Number(response.returncode) === 0) {
    return {
      kind: "error",
      message: typeof data === "string" ? data : "",
      restart: response.restart_required !== false,
    };
  }
  if (
    data &&
    data.otprequired === 1 &&
    data.flow === "admin_password_reset" &&
    data.stage === stage &&
    typeof data.challenge === "string" &&
    /^[a-f0-9]{64}$/.test(data.challenge) &&
    Number.isInteger(data.expires_in) &&
    data.expires_in > 0 &&
    data.expires_in <= 300
  ) {
    return { kind: "challenge", data };
  }
  if (stage === "send_link" && typeof data === "string" && data !== "") {
    return { kind: "complete", message: data, password: "" };
  }
  if (
    stage === "redeem" &&
    Array.isArray(data) &&
    data.length === 2 &&
    data.every((value) => typeof value === "string" && value !== "")
  ) {
    return { kind: "complete", message: data[0], password: data[1] };
  }
  return { kind: "invalid" };
}

export function createPasswordResetFlow(request, options) {
  const { stage, reference = "", now = Date.now } = options;
  if (!["send_link", "redeem"].includes(stage)) {
    throw new Error("Unknown password recovery stage");
  }
  const state = {
    step: "ready",
    busy: false,
    otp: "",
    error: "",
    errorKey: "",
    message: "",
    password: "",
    remainingSeconds: 0,
  };
  let challenge = "";
  let expiresAt = 0;
  let generation = 0;
  let disposed = false;

  function clearChallenge() {
    challenge = "";
    expiresAt = 0;
    state.otp = "";
    state.remainingSeconds = 0;
  }

  function restart(errorKey = "") {
    clearChallenge();
    state.step = "ready";
    state.errorKey = errorKey;
  }

  function tick() {
    if (!challenge || disposed) return;
    state.remainingSeconds = Math.max(0, Math.ceil((expiresAt - now()) / 1000));
    if (state.remainingSeconds === 0) {
      generation++;
      state.busy = false;
      state.error = "";
      restart("EXPIRED");
    }
  }

  async function submit(credentials = {}) {
    if (disposed || state.busy || state.step === "complete") return;
    const wasVerifying = state.step === "otp";
    tick();
    if (wasVerifying && state.step !== "otp") return;
    state.error = "";
    state.errorKey = "";
    if (wasVerifying && !/^[0-9]{6}$/.test(state.otp)) {
      state.errorKey = "INVALID_CODE";
      return;
    }
    const payload = wasVerifying
      ? {
          action: "verifyAdminPasswordResetMfa",
          challenge,
          otpcode: state.otp,
        }
      : stage === "send_link"
      ? {
          action: "resetAdminPassword",
          username: credentials.username,
          emailadmin: credentials.email,
        }
      : { action: "startResetAdminPassword", ref: reference };
    state.otp = "";
    state.busy = true;
    const current = ++generation;
    const requestedAt = now();
    try {
      const response = await request(payload);
      if (disposed || current !== generation) return;
      const result = interpretPasswordResetResponse(response, stage);
      if (result.kind === "challenge" && !wasVerifying) {
        challenge = result.data.challenge;
        // Starting at request time conservatively accounts for transport delay.
        expiresAt = requestedAt + result.data.expires_in * 1000;
        state.step = "otp";
        tick();
      } else if (result.kind === "complete") {
        clearChallenge();
        state.step = "complete";
        state.message = result.message;
        state.password = result.password;
      } else if (result.kind === "error") {
        if (result.restart || !challenge) restart();
        state.error = result.message;
        if (!result.message) state.errorKey = "REQUEST_FAILED";
        tick();
      } else {
        restart("UNEXPECTED_RESPONSE");
      }
    } catch (error) {
      if (!disposed && current === generation) restart("NETWORK_ERROR");
    } finally {
      if (current === generation) state.busy = false;
    }
  }

  function cancel() {
    disposed = true;
    generation++;
    clearChallenge();
    state.busy = false;
    state.step = "ready";
    state.error = "";
    state.errorKey = "";
    state.message = "";
    state.password = "";
  }

  return { state, submit, tick, cancel };
}
